import { apiService } from './api';
import {
  Company,
  CompanyUser,
  CreateCompanyData,
  CompanyFilters,
  CompanyUserFilters,
  ApiResponse,
  PaginatedResponse
} from '../types';

export const companyService = {
  // Company CRUD operations
  async getCompanies(filters?: CompanyFilters): Promise<PaginatedResponse<Company>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiService.get(`/companies?${params.toString()}`);
    return response.data;
  },

  async getCompany(id: string): Promise<ApiResponse<Company>> {
    const response = await apiService.get(`/companies/${id}`);
    return response.data;
  },

  async createCompany(data: CreateCompanyData): Promise<ApiResponse<Company>> {
    const response = await apiService.post('/companies', data);
    return response.data;
  },

  async updateCompany(id: string, data: Partial<CreateCompanyData>): Promise<ApiResponse<Company>> {
    const response = await apiService.put(`/companies/${id}`, data);
    return response.data;
  },

  async deleteCompany(id: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/companies/${id}`);
    return response.data;
  },

  async verifyCompany(id: string, isVerified: boolean, notes?: string): Promise<ApiResponse<Company>> {
    const response = await apiService.post(`/superadmin/companies/${id}/verify`, {
      isVerified,
      notes
    });
    return response.data;
  },

  // Company statistics
  async getCompanyStats(id: string): Promise<ApiResponse<{
    totalProperties: number;
    totalRentalUnits: number;
    totalUsers: number;
    occupiedUnits: number;
    availableUnits: number;
    occupancyRate: string;
    totalRevenue: number;
  }>> {
    const response = await apiService.get(`/companies/${id}/stats`);
    return response.data;
  },

  // Company users management
  async getCompanyUsers(companyId: string, filters?: CompanyUserFilters): Promise<ApiResponse<CompanyUser[]>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiService.get(`/companies/${companyId}/users?${params.toString()}`);
    return response.data;
  },

  async addUserToCompany(companyId: string, data: {
    userId: string;
    role: string;
    permissions?: any[];
    designation?: string;
    department?: string;
  }): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.post(`/companies/${companyId}/users`, data);
    return response.data;
  },

  async updateCompanyUser(companyId: string, userId: string, data: Partial<{
    role: string;
    permissions: any[];
    designation: string;
    department: string;
    isActive: boolean;
  }>): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/companies/${companyId}/users/${userId}`, data);
    return response.data;
  },

  async removeUserFromCompany(companyId: string, userId: string): Promise<ApiResponse> {
    const response = await apiService.delete(`/companies/${companyId}/users/${userId}`);
    return response.data;
  },

  // Company properties and rental units
  async getCompanyProperties(companyId: string): Promise<ApiResponse<any[]>> {
    const response = await apiService.get(`/companies/${companyId}/properties`);
    return response.data;
  },

  async getCompanyRentalUnits(companyId: string): Promise<ApiResponse<any[]>> {
    const response = await apiService.get(`/companies/${companyId}/rental-units`);
    return response.data;
  },

  // Permissions helper
  hasPermission(companyUser: CompanyUser, module: string, action: string): boolean {
    if (!companyUser || !companyUser.permissions) return false;
    
    const modulePermissions = companyUser.permissions.find(p => p.module === module);
    return modulePermissions ? modulePermissions.actions.includes(action as any) : false;
  },

  canAccessModule(companyUser: CompanyUser, module: string): boolean {
    if (!companyUser || !companyUser.permissions) return false;

    return companyUser.permissions.some(p => p.module === module);
  },

  // Subscription management endpoints
  async getFeaturesAndLimitations(companyId: string): Promise<ApiResponse<any>> {
    const response = await apiService.get(`/superadmin/companies/${companyId}/features-and-limitations`);
    return response.data;
  },

  async updateCompanySubscription(companyId: string, subscriptionId: string, instantChange: boolean = false): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/subscription/update`, {
      subscriptionId,
      instantChange
    });
    return response.data;
  },

  async forceUpdateSubscription(companyId: string, subscriptionId: string, updateCycle: boolean = true): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/subscription/force-update`, {
      subscriptionId,
      updateCycle
    });
    return response.data;
  },

  async cancelSubscription(companyId: string, immediate: boolean = false): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/subscription/cancel`, {
      immediate
    });
    return response.data;
  },

  async renewSubscription(companyId: string): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/subscription/renew`);
    return response.data;
  },

  async getAllProductFeatures(): Promise<ApiResponse<any>> {
    const response = await apiService.get('/superadmin/product-features');
    return response.data;
  },

  async addCompanyFeature(companyId: string, featureId: string): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/features/add`, {
      featureId
    });
    return response.data;
  },

  async removeCompanyFeature(companyId: string, featureId: string): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/features/remove`, {
      featureId
    });
    return response.data;
  },

  async updateCompanyLimitations(companyId: string, limitations: {
    maxProperties?: number;
    maxRentalUnits?: number;
    maxUsers?: number;
    maxStorageMB?: number;
  }): Promise<ApiResponse<any>> {
    const response = await apiService.put(`/superadmin/companies/${companyId}/limitations`, {
      limitations
    });
    return response.data;
  },

  async resetCompanyLimitations(companyId: string): Promise<ApiResponse<any>> {
    const response = await apiService.post(`/superadmin/companies/${companyId}/limitations/reset`);
    return response.data;
  }
};